import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { getAuth } from 'firebase/auth';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { EntranceExitEvent } from '../firestore-models/entrance-exit-event';
import { Floor } from '../firestore-models/floor';
import { Exhibit } from '../firestore-models/exhibit';
import { EntranceExitExhibitTransitDownloadFile } from '../firestore-models/entrance-exit-exhibit-transit-download-file';
import { ApiEntranceExitExhibitTransitDownloadFile, ApiSetEntranceExitExhibitTransitDownloadFilesRequest } from '../api-client-module';
import { ProjectLanguage } from '../firestore-models/project-language';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SessionService } from '../session.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';
import { TranslateService } from '@ngx-translate/core';

import { EntranceExitExhibitTransitDownloadDialogComponent } from './entrance-exit-exhibit-transit-download-dialog/entrance-exit-exhibit-transit-download-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';

@Component({
  selector: 'app-entrance-exit-exhibit-transit-download',
  templateUrl: './entrance-exit-exhibit-transit-download.component.html',
  styleUrls: ['./entrance-exit-exhibit-transit-download.component.css'],
  standalone: false,
})
export class EntranceExitExhibitTransitDownloadComponent implements OnInit {

  private eventsCollection!: AngularFirestoreCollection<EntranceExitEvent>;
  eventsObservable!: Observable<EntranceExitEvent[]>;
  eventsSubscribe!: Subscription;
  events!: Array<EntranceExitEvent>;
  private exhibitsCollection!: AngularFirestoreCollection<Exhibit>;
  exhibitsObservable!: Observable<Exhibit[]>;
  exhibitsSubscribe!: Subscription;
  exhibits!: Array<Exhibit>;
  private floorsCollection!: AngularFirestoreCollection<Floor>;
  floorsObservable!: Observable<Floor[]>;
  floorsSubscribe!: Subscription;
  floors!: Array<Floor>;


  //EntranceExitExhibitTransitDownloadFileコレクション
  private downloadFilesCollection!: AngularFirestoreCollection<EntranceExitExhibitTransitDownloadFile>;
  downloadFilesObservable!: Observable<EntranceExitExhibitTransitDownloadFile[]>;
  downloadFilesSubscribe!: Subscription;
  //プロジェクトID
  projectId: string;

  //入退場管理展示来場状況ダウンロードファイル情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'created', 'event_name', 'exhibit_name', 'memo', 'start_date', 'end_date', 'count', 'download'];
  dataSource = new MatTableDataSource<EntranceExitExhibitTransitDownloadFile>();
  selection = new SelectionModel<EntranceExitExhibitTransitDownloadFile>(true, []);
  element!: HTMLElement;

  eventCollectionName = AppConst.FIRESTORE_ENTRANCE_EXIT_EVENTS;

  //各予約設備情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  currentLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private angularFireStorage: AngularFireStorage,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private sessionService: SessionService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    //入退場管理イベント取得
    this.eventsCollection = this.angularFirestore.collection<EntranceExitEvent>(
      AppConst.FIRESTORE_ENTRANCE_EXIT_EVENTS, ref => ref.where('project_id', '==', this.projectId));
    this.eventsObservable = this.eventsCollection.valueChanges({ idField: 'id' });
    this.eventsSubscribe = this.eventsObservable.subscribe(events => {
      this.events = events.sort((a, b) => {
        if (a.open_start_date.seconds > b.open_start_date.seconds ) {
          return -1;
        }
        if (a.open_start_date.seconds < b.open_start_date.seconds ) {
          return 1;
        }
        return 0;
      });
    });
    //フロア・表示グループ取得
    this.floorsCollection = this.angularFirestore.collection<Floor>(
      AppConst.FIRESTORE_FLOORS, ref => ref.where('project_id', '==', this.projectId));
    this.floorsObservable = this.floorsCollection.valueChanges({ idField: 'id' });
    this.floorsSubscribe = this.floorsObservable.subscribe(floors => {
      this.floors = floors;
    });
    //展示取得
    this.exhibitsCollection = this.angularFirestore.collection<Exhibit>(
      AppConst.FIRESTORE_EXHIBITS, ref => ref.where('project_id', '==', this.projectId));
    this.exhibitsObservable = this.exhibitsCollection.valueChanges({ idField: 'id' });
    this.exhibitsSubscribe = this.exhibitsObservable.subscribe(exhibits => {
      const loginUser = this.sessionService.session.user!;
      const selectExhibits = loginUser.exhibit_ids.filter(exhibitId => exhibitId.project_id === this.projectId);
      if (!!selectExhibits && selectExhibits.length > 0) {
        const exhibitIds = selectExhibits[0].exhibit_ids;
        this.exhibits = exhibits.filter(exhibit => exhibitIds.includes(exhibit.id!));
      }
    });

    const authUser = getAuth().currentUser!;
    this.downloadFilesCollection = this.angularFirestore.collection<EntranceExitExhibitTransitDownloadFile>(
      AppConst.FIRESTORE_ENTRANCE_EXIT_EXHIBIT_TRANSIT_DOWNLOAD_FILES, ref => ref.where('project_id', '==', this.projectId).where('uid', '==', authUser.uid).orderBy('created', 'desc'));
    this.downloadFilesObservable = this.downloadFilesCollection.valueChanges({ idField: 'id' });
    this.downloadFilesSubscribe = this.downloadFilesObservable.subscribe(files => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.dataSource.data = this.addFilterValue(files);
    });

    this.currentLang = this.translate.currentLang;
  }

  ngOnDestroy(): void {
    this.eventsSubscribe.unsubscribe();
    this.exhibitsSubscribe.unsubscribe();
    this.downloadFilesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: EntranceExitExhibitTransitDownloadFile): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(downloadFile: EntranceExitExhibitTransitDownloadFile): void {
    const startDate: any = downloadFile!.start_date ? new Date(downloadFile!.start_date.seconds * 1000) : null;
    const endDate: any = downloadFile!.end_date ? new Date(downloadFile!.end_date.seconds * 1000) : null;
    let apiDownloadFile: ApiEntranceExitExhibitTransitDownloadFile = {
      id: downloadFile.id,
      start_date: startDate,
      end_date: endDate,
      entrance_exit_event_id: downloadFile.entrance_exit_event_id,
      exhibit_id: downloadFile.exhibit_id,
      uid: downloadFile.uid,
      memo: downloadFile.memo,
      project_id: downloadFile.project_id,
      enabled: downloadFile.enabled
    };
    const dialogRef = this.dialog.open(EntranceExitExhibitTransitDownloadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, downloadFile: apiDownloadFile, events: this.events, floors: this.floors, exhibits: this.exhibits }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(EntranceExitExhibitTransitDownloadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, downloadFile: null, events: this.events, floors: this.floors, exhibits: this.exhibits }
    });
  }
  
  //削除
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedDownloadFiles: Array<ApiEntranceExitExhibitTransitDownloadFile> = new Array<ApiEntranceExitExhibitTransitDownloadFile>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedDownloadFile: ApiEntranceExitExhibitTransitDownloadFile = {
            id: row.id,
            entrance_exit_event_id: row.entrance_exit_event_id,
            exhibit_id: row.exhibit_id,
            uid: row.uid,
            start_date: row.start_date.toDate(),
            end_date: row.end_date.toDate(),
            memo: row.memo,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedDownloadFiles.push(selectedDownloadFile);
        }
      }
      if (!!result) {
        let request: ApiSetEntranceExitExhibitTransitDownloadFilesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          files: selectedDownloadFiles
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setEntranceExitExhibitTransitDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  
    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'event_name':
          return compare(this.getEventName(a), this.getEventName(b), isAsc);
        case 'exhibit_name':
          return compare(this.getExhibitName(a), this.getExhibitName(b), isAsc);
        case 'memo':
          return compare(a.memo, b.memo, isAsc);
        case 'count':
          return compare(a.row_count, b.row_count, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      filterValue += doc.memo + ' '
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  download(downloadFile: EntranceExitExhibitTransitDownloadFile): void {
    const path = `entrance_exit_exhibit_transit/${downloadFile.project_id}/${downloadFile.file_name}`;
    const storageRef = this.angularFireStorage.ref(path);
    const url = storageRef.getDownloadURL().subscribe((url) => {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.download = downloadFile.file_name!;
      a.href = url!;
      a.click();
      document.body.removeChild(a);
    });
  }

  getEventName(downloadFile: EntranceExitExhibitTransitDownloadFile): string {
    let eventName = '';
    for (const event of this.events) {
      if (downloadFile.entrance_exit_event_id === event.id) {
        eventName = event.names[0].name;
        break;
      }
    }
    return eventName;
  }

  getExhibitName(downloadFile: EntranceExitExhibitTransitDownloadFile): string {
    let exhibitName = '';
    for (const exhibit of this.exhibits) {
      if (downloadFile.exhibit_id === exhibit.id) {
        exhibitName = exhibit.exhibit_names[0].name;
        break;
      }
    }
    return exhibitName;
  }

}
